import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import blogApi from '../api/blogApi';
import { BlogPost, LoadingStatus } from '../../types';

interface BlogState {
  posts: BlogPost[];
  selectedPost: BlogPost | null;
  total: number;
  status: LoadingStatus;
  postStatus: LoadingStatus;
  error: string | null;
}

const initialState: BlogState = {
  posts: [],
  selectedPost: null,
  total: 0,
  status: 'idle',
  postStatus: 'idle',
  error: null,
};

export const fetchBlogPosts = createAsyncThunk(
  'blog/fetchAll',
  async (params: { limit?: number; skip?: number } | undefined, { rejectWithValue }) => {
    try {
      const res = await blogApi.listPosts(params);
      return res as { data: { items: BlogPost[]; total: number } };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load posts');
    }
  },
);

export const fetchBlogPost = createAsyncThunk(
  'blog/fetchOne',
  async (slug: string, { rejectWithValue }) => {
    try {
      const res = await blogApi.getPost(slug);
      return res.data as BlogPost;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Post not found');
    }
  },
);

const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    clearSelectedPost(state) {
      state.selectedPost = null;
      state.postStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlogPosts.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchBlogPosts.fulfilled, (state, { payload }) => {
        state.posts = payload.data.items;
        state.total = payload.data.total;
        state.status = 'succeeded';
      })
      .addCase(fetchBlogPosts.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(fetchBlogPost.pending, (state) => { state.postStatus = 'loading'; })
      .addCase(fetchBlogPost.fulfilled, (state, { payload }) => {
        state.selectedPost = payload;
        state.postStatus = 'succeeded';
      })
      .addCase(fetchBlogPost.rejected, (state, { payload }) => {
        state.postStatus = 'failed';
        state.error = payload as string;
      });
  },
});

export const { clearSelectedPost } = blogSlice.actions;
export default blogSlice.reducer;

export const selectBlogPosts = (state: { blog: BlogState }) => state.blog.posts;
export const selectBlogTotal = (state: { blog: BlogState }) => state.blog.total;
export const selectBlogStatus = (state: { blog: BlogState }) => state.blog.status;
export const selectSelectedPost = (state: { blog: BlogState }) => state.blog.selectedPost;
export const selectPostStatus = (state: { blog: BlogState }) => state.blog.postStatus;
